<script lang="ts">
  import {
    computePosition,
    flip,
    shift,
    size,
    type Placement,
    autoUpdate,
    offset as floatingUiOffset,
  } from '@floating-ui/dom'
  import { onDestroy } from 'svelte'

  export let activator: HTMLElement | undefined
  export let placement: Placement = 'bottom'
  export let visible = false
  export let contained = false
  export let timeout: number | undefined = undefined
  export let offset: number | undefined = undefined

  let tooltip: HTMLElement | undefined
  let cleanup: (() => void) | undefined = undefined

  $: if (visible && timeout) {
    setTimeout(() => {
      // eslint-disable-next-line svelte/infinite-reactive-loop
      visible = false
    }, timeout)
  }

  $: if (tooltip && activator) {
    createTooltip()
  }

  async function updatePosition(activator: HTMLElement, tooptip: HTMLElement) {
    const { x, y } = await computePosition(activator, tooptip, {
      placement,
      middleware: [
        floatingUiOffset(offset),
        shift(),
        flip(),
        size({
          apply({ elements, rects }) {
            Object.assign(elements.floating.style, {
              minWidth: contained ? `${rects.reference.width}px` : undefined,
              maxWidth: contained ? `${rects.reference.width}px` : undefined,
            })
          },
          padding: {
            top: 8,
            bottom: 24,
            left: 8,
            right: 8,
          },
        }),
      ],
    })

    Object.assign(tooptip.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  async function createTooltip() {
    if (activator && tooltip) {
      cleanup = autoUpdate(activator, tooltip, () =>
        updatePosition(activator as HTMLElement, tooltip as HTMLElement),
      )
    } else if (cleanup) {
      cleanup()
    }
  }

  onDestroy(() => cleanup && cleanup())
</script>

<div class="container" class:visible bind:this={tooltip}>
  <slot />
</div>

<style lang="scss">
  .container {
    position: absolute;
    box-sizing: border-box;
    display: flex;
    width: max-content;
    background-color: rgba(0, 0, 0, 0.75);
    color: #fff;
    font-weight: initial;
    font-size: initial;
    border-radius: 12px;
    padding: 0.75em 1em;
    z-index: 20;
    opacity: 0;
    pointer-events: none;
    transition: 0.25s opacity cubic-bezier(0.4, 0, 0.2, 1);
  }

  .container.visible {
    opacity: 1;
    pointer-events: unset;
  }
</style>
